import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { TableCell, TableRow } from '@mui/material';

import OnInfoIcon from '../../../icons/OnInfoIcon';
import {
    loadAttendanceDetails,
    loadContractDetailsForContractId,
    loadServiceDetailsForContractId
} from '../../../store/directusService';
import { loadParticipantByPtCode } from '../../../store/participantService';
import { isTimePast } from '../../../utils/dateFunctions';
import SingleSelect from '../../formElements/SingleSelect';

import table from '../../../commonStyles/tables.module.css';
import local from '../courseStyles/fullCourseAttendanceStyles.module.css';

const FullCourseAttendanceRow = ({ row, module, showCells, onUpdateParticipantDetail, isSelfEmployedCoach }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // LOCAL STATE
    const [attendanceStatus, setAttendanceStatus] = useState({});
    const [isDisabled, setIsDisabled] = useState(false);
    const [isPTRequestSent, setIsPTRequestSent] = useState(false);

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const attendanceDetails = useSelector(
        (state) => state.entities.directusService.attendanceDetails
    );
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const contractDetailsById = useSelector(
        (state) => state.entities.directusService.contractDetailsById
    );
    const servicesByContractId = useSelector(
        (state) => state.entities.directusService.servicesByContractId
    );

    // USEEFFECTS
    useEffect(() => {
        setAttendanceStatus({});
        if (attendanceDetails?.length < 1) dispatch(loadAttendanceDetails());
        if (!module) return;

        const dateTime = `${module?.date}T${module?.startTime}`;
        if (!isTimePast(dateTime)) setIsDisabled(true);
    }, []);

    useEffect(() => {
        if (attendanceDetails?.length < 1) return;
        const participantStatus = attendanceDetails.find(
            (el) =>
                el.id ===
                module?.participants?.find((entry) => entry.participantId === row.id)?.statusId
        );
        setAttendanceStatus(participantStatus);
        if (participantStatus?.name?.toLowerCase() !== 'not known') setIsDisabled(true);
    }, [attendanceDetails, module]);

    useEffect(() => {
        if (isPTRequestSent && currentParticipant.ptCode === row.ptCode) {
            if (!(currentParticipant.contractId in contractDetailsById))
                dispatch(loadContractDetailsForContractId(currentParticipant.contractId));
            if (!(currentParticipant.contractId in servicesByContractId))
                dispatch(loadServiceDetailsForContractId(currentParticipant.contractId));
        }
    }, [isPTRequestSent, currentParticipant]);

    useEffect(() => {
        if (
            isPTRequestSent &&
            currentParticipant?.ptCode === row.ptCode &&
            contractDetailsById[currentParticipant.contractId] &&
            servicesByContractId[currentParticipant.contractId]
        )
            navigate('/edit_participant');
    }, [isPTRequestSent, contractDetailsById, servicesByContractId, currentParticipant]);

    // EVENT HANDLERS
    const onStatusIdChange = (id) => {
        if (attendanceStatus?.id && attendanceStatus.id !== id)
            onUpdateParticipantDetail(id, row.id);
        const selected = attendanceDetails.find((el) => el.id === id);
        selected && setAttendanceStatus(selected);
    };

    const onClickPTCode = () => {
        if (!row?.ptCode) return;
        dispatch(loadParticipantByPtCode(row.ptCode));
        setIsPTRequestSent(true);
    };

    // RENDER
    return (
        <TableRow
            key={row.id}
            hover
            className={`${showCells ? table.basicRowStripes : table.noStripes} ${table.rowHeight}`}>
            <TableCell className={table.rowLink} onClick={onClickPTCode}>
                {row.ptCode}
            </TableCell>
            {showCells && <TableCell> {row.firstName}</TableCell>}
            {showCells && <TableCell>{row.lastName}</TableCell>}
            {showCells && (
                <TableCell className={local.minWidthCell}>
                    <SingleSelect
                        id="attendanceStatus"
                        placeholder="Select status..."
                        disabled={isDisabled || (isSelfEmployedCoach && loggedInUser?.id !== module.userId)}
                        customClass="tableRowSelector"
                        menuItems={attendanceDetails || []}
                        selectedId={attendanceStatus?.id || ''}
                        selected={
                            attendanceDetails.find((el) => el.id === attendanceStatus?.id) || {}
                        }
                        onChange={(chosenId) => onStatusIdChange(chosenId)}
                    />
                </TableCell>
            )}
            {showCells && (
                <TableCell>{row.notes ? <OnInfoIcon content={row.notes} /> : <span />}</TableCell>
            )}
        </TableRow>
    );
};

FullCourseAttendanceRow.propTypes = {
    row: PropTypes.shape({
        id: PropTypes.string.isRequired,
        ptCode: PropTypes.string.isRequired,
        contractId: PropTypes.string.isRequired,
        firstName: PropTypes.string.isRequired,
        lastName: PropTypes.string.isRequired,
        notes: PropTypes.string.isRequired
    }).isRequired,
    module: PropTypes.object,
    showCells: PropTypes.bool.isRequired,
    onUpdateParticipantDetail: PropTypes.func,
    isSelfEmployedCoach: PropTypes.bool
};

export default FullCourseAttendanceRow;
